/* global APP_VERSION */
import PropTypes from 'prop-types'
import React from 'react'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import '../../polyfill'
import { ToastProvider } from '@vtex/styleguide/lib/ToastProvider'

import { IntlSaveComponent } from '../../i18n/utils'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'
import Sidebar from '@/checkout-instore-legacy/components/Commons/Sidebar'
import NotifierContainer from '@/checkout-instore-legacy/components/Commons/Notifier/NotifierContainer'
import NetworkMessage from '@/checkout-instore-legacy/components/Network/NetworkMessageContainer'
import { ErrorBoundary } from '@/checkout-instore-legacy/error/components'
import { ErrorBoundaryInfo } from '@/checkout-instore-legacy/info/components'
import RootModalContainer from '@/checkout-instore-legacy/rootModal/containers/RootModalContainer'
import { reactComponentError } from '@/checkout-instore-legacy/utils/EventHelper'
import {
  goToCartCopy,
  goToCartChange,
} from '@/checkout-instore-legacy/utils/routesUtils'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'
import { SimpleModalContainer } from '@/checkout-instore-legacy/components/Commons/Elements/SimpleModal'
import { ModalContainer as ScheduledDeliveryModalContainer } from '@vtexlab/scheduled-delivery'
import { PopoverContainer } from '@/checkout-instore-legacy/components/Commons/Popover'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import logRocket from '@/checkout-instore-legacy/utils/LogRocket'
import { validate } from '@/checkout-instore-legacy/utils/tracking/validations'
import { currentDomainMatchEnv } from '@/checkout-instore-legacy/utils/EnvUtils'
import { getNewDomainErrorMessage } from '@/checkout-instore-legacy/globalTopMessage/utils'
import { healthcheck } from '@/checkout-instore-legacy/healthcheck/service'
import * as FS from '@fullstory/browser'
import { FS_INIT_PROPS } from '@/checkout-instore-legacy/constants/EnvConstants'

import '@/checkout-instore-legacy/fetchers/index'
import { checkoutInstoreCustomJs } from '@/checkout-instore-legacy/constants/CustomScriptConstants'
import GlobalTopMessageContainer from '@/checkout-instore-legacy/globalTopMessage/containers/GlobalTopMessageContainer'
// TODO: Re-activate admin when it is recreated
// import loadAdminCustomSettings from 'utils/adminCustomSettings'

import smoothscroll from 'smoothscroll-polyfill'
import '@/checkout-instore-legacy/styles/main.less'
import { sendProxyConfigEvent } from '@/checkout-instore-legacy/utils/setProxyConfig'

import { AppTopBar } from './AppTopBar'
import hotjar from '@/checkout-instore-legacy/utils/hotjar'

class App extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isSidebarOpen: false,
      nativeAppInfo: null,
    }

    this.runInstoreOnThisDomain = currentDomainMatchEnv()

    this.resourcesTimeout = setTimeout(() => {
      this.props.acknowledgeResources({
        loader: { available: false },
      })
    }, 1000)

    smoothscroll.polyfill()
  }

  clientProfileChange = (event) => {
    if (
      !event ||
      !event.data ||
      !event.data.clientProfileData ||
      !this.props.changeCustomer
    ) {
      return
    }

    this.props.changeCustomer(event.data.clientProfileData)
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.domainValidations()
    }
  }

  componentDidMount() {
    if (typeof window === 'undefined') {
      return
    }

    this.props.setAppVersion(APP_VERSION)
    WebViewBridge.initWebViewBridge()
    WebViewBridge.listen(this.onReceiveWebViewBridgeEvent)

    window.reloadOrderForm = this.props.reloadOrderForm

    window.vtexInstore = window.vtexInstore || {}
    window.vtexInstore.devices = window.vtexInstore.devices || {}
    window.vtexInstore.devices.setDeviceId = this.props.setDeviceId

    window.addEventListener(
      'openRootModal.instore',
      this.props.openRootModal,
      false
    )
    window.addEventListener(
      'openRootModalWithError.instore',
      this.props.openRootModalWithError,
      false
    )
    window.addEventListener(
      'loadCustomer.instore',
      this.handleLoadCustomer,
      false
    )
    window.addEventListener(
      'setClientProfileData.instore',
      this.handleSetClientProfileData,
      false
    )
    window.addEventListener('vendorIdentified', this.handleVendorChanged, false)

    CustomEvent.sendEvent('load.instore')
    window.inStoreIsLoaded = true

    CustomEvent.listenEvent('load.instore-custom', (event) => {
      if (event && event.data && event.data.src === checkoutInstoreCustomJs) {
        logRocket.init()
        hotjar.init()
        if (getInstoreConfig().isFullStoryEnabled && validate()) {
          FS.init(FS_INIT_PROPS)
        }
        sendProxyConfigEvent()
      }
    })
    CustomEvent.listenEvent('clientProfile.changed', this.clientProfileChange)

    // Domain validations
    this.domainValidations()

    // Healthcheck call
    healthcheck()
    this.props.refreshAccountHostName()
  }

  componentWillUnmount() {
    WebViewBridge.unlisten(this.onReceiveWebViewBridgeEvent)
    window.reloadOrderForm = undefined
    window.removeEventListener(
      'openRootModal.instore',
      this.props.openRootModal
    )
    window.removeEventListener(
      'openRootModalWithError.instore',
      this.props.openRootModalWithError
    )
    window.removeEventListener('loadCustomer.instore', this.handleLoadCustomer)
    window.removeEventListener(
      'setClientProfileData.instore',
      this.handleSetClientProfileData
    )
    window.removeEventListener(
      'vendorIdentified',
      this.handleVendorChanged,
      false
    )

    CustomEvent.unlistenEvent('changeLocaleMessages', this.changeLocaleMessages)
    CustomEvent.unlistenEvent('clientProfile.changed', this.clientProfileChange)
  }

  domainValidations() {
    if (!this.runInstoreOnThisDomain) {
      this.props.setGlobalTopMessage(getNewDomainErrorMessage())
      return
    }

    this.props.clearGlobalTopMessage()
  }

  handleLoadCustomer = (e) => {
    if (e && e.detail && e.detail.identification) {
      this.props.setCustomer({
        identification: e.detail.identification,
        router: this.props.router,
      })
    } else {
      console.error(
        'Event should contain detail.identification property defined'
      )
    }
  }

  handleSetClientProfileData = (e) => {
    if (e && e.detail && e.detail.data) {
      this.props.setClientProfileData(e.detail.data)
    } else {
      console.error('Event should contain detail.data property defined')
    }
  }

  handleVendorChanged = (e) => {
    if (e && e.data && e.data.vendor) {
      const vendor = e.data.vendor
      const vendorId = vendor && vendor.id
      if (getInstoreConfig().transferEnabled) {
        this.props.updateTransferVendor(vendorId)
      }
    } else {
      console.error('Event should contain data.vendor property defined')
    }
  }

  onReceiveWebViewBridgeEvent = (event) => {
    switch (event.type) {
      case 'vendorLogout':
        this.props.logout(this.props.router)
        break
      case 'clearCart':
        this.props.newOrder()
        break
      case 'copyCart':
        goToCartCopy(
          this.props.router,
          event.data.orderFormId,
          event.data.params
        )
        break
      case 'changeCart':
        goToCartChange(
          this.props.router,
          event.data.orderFormId,
          event.data.params
        )
        break
      case 'goHome':
        this.props.router.push('/')
        break
      case 'goToRoute':
        if (event.data && event.data.route) {
          this.props.router.push(event.data.route)
        }
        break
      case 'acknowledgeResources':
        clearTimeout(this.resourcesTimeout)
        this.props.acknowledgeResources(event.data.resources)
        break
      case 'appInfo':
        this.setState({ nativeAppInfo: event.data })
        this.props.dispatchNativeAppInfo(event.data)
        this.props.setAppInfo(event.data)
        break
      case 'deviceId':
        event.data && this.props.setDeviceId(event.data.deviceId)
        break
      case 'appKeyAndAppToken':
        this.props.setCredentials(event.data)
        break
      case 'credentialsSuccess':
        this.props.setCredentialsSuccess()
        break
      case 'credentialsError':
        this.props.setCredentialsError(event.data && event.data.error)
        break
      case 'appState':
        this.props.setAppState(event.data && event.data.appState)
        break
      default:
        console.log('webviewBridge received event', event)
    }
  }

  setSidebarVisibility = (isOpen) => {
    this.setState({ isSidebarOpen: isOpen })
  }

  render() {
    const { isSidebarOpen } = this.state
    const { children, vendor, location } = this.props

    const { showNetworkMessage } = getInstoreConfig()

    const currentRoute = {
      ...location,
      name: location.pathname,
    }
    const { name: routeName } = currentRoute

    const routeClassName = routeName ? ` path-${routeName}` : ''
    const sellerClassName =
      vendor && vendor.franchiseAccount
        ? ` seller-${vendor.franchiseAccount}`
        : ''
    const containerClassName = `instore-app-container${routeClassName}${sellerClassName}`

    return (
      <ErrorBoundary
        onError={reactComponentError}
        FallbackComponent={ErrorBoundaryInfo}
      >
        <IntlSaveComponent />
        {!this.runInstoreOnThisDomain && (
          <div className={containerClassName}>
            <GlobalTopMessageContainer />
          </div>
        )}
        {this.runInstoreOnThisDomain && (
          <ToastProvider>
            <div className={containerClassName}>
              <GlobalTopMessageContainer />
              <Sidebar
                isOpen={isSidebarOpen}
                setSidebarVisibility={this.setSidebarVisibility}
                router={this.props.router}
                nativeAppInfo={this.state.nativeAppInfo}
              />
              <div className="flex flex-column vh-100">
                {showNetworkMessage && (
                  <div className="flex flex-column vw-100">
                    <NetworkMessage />
                  </div>
                )}
                <AppTopBar
                  setSidebarVisibility={this.setSidebarVisibility}
                  location={this.props.location}
                  currentPathname={this.props.match.path}
                />
                <div
                  className="flex flex-column flex-auto overflow-auto"
                  id="content-container"
                >
                  {children}
                </div>
                <SimpleModalContainer />
                <ScheduledDeliveryModalContainer />
                <PopoverContainer />
                <NotifierContainer router={this.props.router} />
              </div>
              <RootModalContainer />
            </div>
          </ToastProvider>
        )}
      </ErrorBoundary>
    )
  }
}

App.propTypes = {
  children: PropTypes.any,
  router: PropTypes.any,
  location: PropTypes.any,
  match: PropTypes.any,
  changeCustomer: PropTypes.func,
  updateTransferVendor: PropTypes.func.isRequired,
  updateTransferCart: PropTypes.func.isRequired,
  updateTransferOrderFormId: PropTypes.func.isRequired,
  transferData: PropTypes.object.isRequired,
  setAppInfo: PropTypes.func,
  setDeviceId: PropTypes.func,
  setCredentials: PropTypes.func.isRequired,
  setCustomer: PropTypes.func.isRequired,
  setClientProfileData: PropTypes.func.isRequired,
  reloadOrderForm: PropTypes.func.isRequired,
  setAppVersion: PropTypes.func.isRequired,
  setCredentialsSuccess: PropTypes.func.isRequired,
  setCredentialsError: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  vendor: PropTypes.object,
  openRootModal: PropTypes.func.isRequired,
  openRootModalWithError: PropTypes.func.isRequired,
  newOrder: PropTypes.func.isRequired,
  acknowledgeResources: PropTypes.func.isRequired,
  dispatchNativeAppInfo: PropTypes.func.isRequired,
  setGlobalTopMessage: PropTypes.func.isRequired,
  clearGlobalTopMessage: PropTypes.func.isRequired,
  refreshAccountHostName: PropTypes.func.isRequired,
  setAppState: PropTypes.func.isRequired,
}

export default withRouter(App)
